// Barcode.js

import { BarcodeFormat, DecodeHintType } from '@zxing/library';

export const BARCODE_SCAN_FORMATS = Object.freeze([
  BarcodeFormat.EAN_13,
  BarcodeFormat.EAN_8,
  BarcodeFormat.UPC_A,
  BarcodeFormat.UPC_E,
  BarcodeFormat.CODE_128,
  BarcodeFormat.CODE_39,
]);

export const QR_CODE_SCAN_FORMATS = Object.freeze([
  BarcodeFormat.QR_CODE,
]);

export const requestCameraPermission = async (constraints = {
  audio: false,
  video: {
    facingMode: {
      ideal: 'environment',
    },
  },
}) => {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
    return null;
  }

  return navigator.mediaDevices.getUserMedia(constraints);
};

export const stopMediaStreamTracks = (stream) => {
  if (!stream || typeof stream.getTracks !== 'function') return;

  stream.getTracks().forEach((track) => {
    if (track && typeof track.stop === 'function') {
      track.stop();
    }
  });
};

export const normalizeBarcodeValue = (value) => String(value ?? '').trim();

export const buildBarcodeDecodeHints = (formats = BARCODE_SCAN_FORMATS) => {
  const hints = new Map();
  hints.set(DecodeHintType.POSSIBLE_FORMATS, Array.isArray(formats) && formats.length ? formats : BARCODE_SCAN_FORMATS);
  return hints;
};

export const resolvePreferredVideoDeviceId = (devices, currentDeviceId = '') => {
  const rows = Array.isArray(devices) ? devices : [];
  const current = String(currentDeviceId || '').trim();

  if (current && rows.some((device) => device?.deviceId === current)) {
    return current;
  }

  const preferred = rows.find((device) => {
    const label = String(device?.label || '').toLowerCase();
    return label.includes('back') || label.includes('rear') || label.includes('environment');
  });

  return String(preferred?.deviceId || rows[0]?.deviceId || '').trim();
};

export const isIgnorableBarcodeScanError = (error) => {
  const errorName = String(error?.name || '').trim();
  return errorName === 'NotFoundException'
    || errorName === 'ChecksumException'
    || errorName === 'FormatException';
};

export const resolveBarcodeCameraError = (error) => {
  const errorName = String(error?.name || '').trim();

  if (errorName === 'NotAllowedError' || errorName === 'SecurityError') {
    return 'Camera access was blocked. Allow camera access to scan a barcode.';
  }

  if (errorName === 'NotFoundError' || errorName === 'DevicesNotFoundError') {
    return 'No camera was found on this device.';
  }

  if (errorName === 'NotReadableError' || errorName === 'TrackStartError') {
    return 'The camera is already in use by another app.';
  }

  if (errorName === 'OverconstrainedError' || errorName === 'ConstraintNotSatisfiedError') {
    return 'The selected camera is not available.';
  }

  return 'Unable to start barcode scanning on this device.';
};